import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import router from "@/router";
import { clearDonorListViewState } from "@/utils/donorListViewState";

export interface User {
  name: string;
  surname: string;
  email: string;
  password: string;
  token: string;
}

export interface UserAuthInfo {
  errors: Array<string>;
  homePage: string;
  user: User;
  isAuthenticated: boolean;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = [] as Array<string>;
  errorCode = 0;
  homePage = "dashboard";
  timeout = null;
  user = {} as User;
  isIdle = false;
  permissions = [] as Array<string>;
  isAuthenticated = !!JwtService.getToken();
  idleVue = { isIdle: false };

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  /**
   * Get current user object
   * @returns Boolean
   */
  get getIsIdle(): boolean {
    return this.isIdle;
  }
  /**
   *
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  get getPermissions(): Array<string> {
    return this.permissions;
  }

  get getHomePage(): string {
    return this.homePage;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors(): Array<string> {
    return this.errors;
  }

  get getErrorCode(): number {
    return this.errorCode;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    const raw = error?.errors;
    if (Array.isArray(raw)) {
      this.errors = raw.map(String);
    } else if (raw !== null && raw !== undefined && typeof raw === "object") {
      // Flatten Laravel validation object: { field: ['msg', ...], ... } → ['msg', ...]
      this.errors = (Object.values(raw) as unknown[]).flatMap((v) =>
        Array.isArray(v) ? v.map(String) : [String(v)]
      );
    } else if (typeof raw === "string" && raw.length > 0) {
      this.errors = [raw];
    } else {
      this.errors = ["Ocurrió un error inesperado. Intente nuevamente."];
    }
    this.errorCode = error?.errorCode ?? 0;
  }

  @Mutation
  [Mutations.SET_AUTH](data) {
    const normalizedData = data ?? {};
    const payload = normalizedData.data ?? normalizedData;
    const permissions = Array.isArray(normalizedData.permissions)
      ? normalizedData.permissions
      : Array.isArray(payload.permissions)
      ? payload.permissions
      : [];
    const token = normalizedData.token ?? payload.token;

    this.isAuthenticated = true;
    this.user = (payload.user ?? {}) as User;
    this.homePage =
      typeof payload.homePage === "string" ? payload.homePage : "dashboard";
    this.permissions = permissions;
    this.errors = [];
    if (token) {
      JwtService.saveToken(token);

      ApiService.setHeader(token);
    }
  }

  @Mutation
  [Mutations.SET_USER](user) {
    this.user = user;
  }

  @Mutation
  [Mutations.SET_PASSWORD](password) {
    this.user.password = password;
  }

  @Mutation
  [Mutations.IDLE_VUE_CHANGED](isIdle) {
    this.isIdle = isIdle;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    this.permissions = [];
    this.homePage = "dashboard";
    JwtService.destroyToken();
  }

  @Action
  [Actions.LOGIN](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);

          const token = data?.token ?? data?.data?.token;
          if (token) {
            ApiService.setHeader(token);
          }
          /** Borramos los filtros que se hayan guardado **/
          localStorage.setItem("donorFilters", "");
          clearDonorListViewState();
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [Actions.UPDATE_PASSWORD](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, {
            errors: response.data.errors,
            errorCode: response.status,
          });
          reject();
        });
    });
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.commit(Mutations.PURGE_AUTH);
    router.push("/login");
  }

  @Action
  [Actions.REGISTER](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("registration", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth/password/reset", payload)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.RESET_PASSWORD](payload) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth/password/reset/confirm", payload)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.VERIFY_AUTH]() {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      return ApiService.get("auth")
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
        })
        .catch(() => {
          this.context.dispatch(Actions.LOGOUT);
          this.context.commit(Mutations.SET_ERROR, "");
        });
    } else {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }

  @Action
  [Actions.UPDATE_USER](payload) {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.post("update_user", payload)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_USER, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }
}
